import { Alert, AlertDescription } from "@/components/ui/alert";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { FloatingInput } from "@/components/ui/floating-input";
import { trpc } from "@/utils/trpc";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, FlaskConical, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useConnectDispatch, useConnectState } from "../ConnectContext";
import ConnectStep from "../components/ConnectStep";
import { InstitutionHeader } from "../components/InstitutionHeader";
import { ActionType } from "../types";

const AuthStep = () => {
  const authenticateMutation = trpc.connect.authenticate.useMutation();
  const verifyMutation = trpc.connect.verify.useMutation();
  const grantAccessMutation = trpc.connect.grantAccess.useMutation();

  const { institution, connectSessionId, isReconnect, appConfig } =
    useConnectState();
  const dispatch = useConnectDispatch();

  const [stage, setStage] = useState<"auth" | "verify">("auth");
  const [fieldSpecs, setFieldSpecs] = useState(institution!.authFields);
  const [hasCredentialsError, setHasCredentialsError] =
    useState<boolean>(false);

  const makeDefaultValues = (fields: typeof fieldSpecs) => {
    const values: Record<string, string> = {};
    fields.forEach((field) => {
      values[field.key] = "";
    });
    return values;
  };

  const form = useForm({
    defaultValues: makeDefaultValues(fieldSpecs),
  });

  const handleAuthSubmit = async (data: Record<string, string>) => {
    const authRes = await authenticateMutation.mutateAsync({
      connectSessionId: connectSessionId!,
      institutionId: institution!.id,
      credentials: data,
    });

    if (!authRes.success) {
      return setHasCredentialsError(true);
    }

    setHasCredentialsError(false);

    if (authRes.verificationRequired) {
      if (!authRes.verificationFields) {
        return console.error("verificationFields not found");
      }

      form.reset(makeDefaultValues(authRes.verificationFields));
      setFieldSpecs(authRes.verificationFields);
      setStage("verify");
    } else {
      // For reconnect, complete the flow immediately
      if (isReconnect) {
        await completeReconnect();
      } else {
        dispatch({
          type: ActionType.SetAuthenticated,
          payload: {
            needsVerification: false,
          },
        });
      }
    }
  };

  const completeReconnect = async () => {
    try {
      const result = await grantAccessMutation.mutateAsync({
        connectSessionId: connectSessionId!,
      });

      dispatch({
        type: ActionType.CompleteReconnect,
        payload: {
          connectionId: result.connectionId,
        },
      });
    } catch (error) {
      setHasCredentialsError(true);
      console.error("Reconnect failed:", error);
    }
  };

  const handleVerifySubmit = async (data: Record<string, string>) => {
    const verifyRes = await verifyMutation.mutateAsync({
      connectSessionId: connectSessionId!,
      payload: data,
    });

    if (!verifyRes.success) {
      return setHasCredentialsError(true);
    }

    setHasCredentialsError(false);

    if (verifyRes.verificationRequired) {
      if (!verifyRes.verificationFields) {
        return console.error("verificationFields not found");
      }

      form.reset(makeDefaultValues(verifyRes.verificationFields));
      setFieldSpecs(verifyRes.verificationFields);
      return;
    }

    // For reconnect, complete the flow immediately after verification
    if (isReconnect) {
      await completeReconnect();
    } else {
      dispatch({
        type: ActionType.SetVerified,
        payload: {
          verified: true,
        },
      });
    }
  };

  const onSubmit = async (data: Record<string, string>) => {
    const payload = Object.fromEntries(
      fieldSpecs.map((field) => [field.key, data[field.key] ?? ""]),
    );
    if (stage === "auth") {
      return handleAuthSubmit(payload);
    } else if (stage === "verify") {
      return handleVerifySubmit(payload);
    }
  };

  const isLoading =
    authenticateMutation.isLoading ||
    verifyMutation.isLoading ||
    grantAccessMutation.isLoading;

  const hasError =
    authenticateMutation.isError ||
    verifyMutation.isError ||
    grantAccessMutation.isError;

  return (
    <ConnectStep
      stepId={stage === "auth" ? "auth" : "verify"}
      nextButtonLabel="Ingresar"
      nextLoading={isLoading}
      onClickNext={form.handleSubmit(onSubmit)}
    >
      {/* Bank header */}
      <div className="mb-5">
        <div className="mb-3">
          <InstitutionHeader colors={institution} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
        >
          <h2 className="text-xl font-semibold tracking-tight">
            Ingresa a{" "}
            <span className="text-bank-primary">{institution?.name}</span>
          </h2>
          <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
            <ShieldCheck className="w-4 h-4 text-green-600" />
            <span>Conexión segura y encriptada</span>
          </div>
        </motion.div>
      </div>

      {/* Error alerts */}
      <AnimatePresence mode="wait">
        {hasError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4"
          >
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Un error inesperado ha occurrido. Por favor, intente mas tarde.
              </AlertDescription>
            </Alert>
          </motion.div>
        )}

        {hasCredentialsError && !hasError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4"
          >
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Ocurrió un error al ingresar a {institution?.name}. Revise sus
                credenciales e intente de nuevo.
              </AlertDescription>
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form */}
      <AnimatePresence mode="wait">
        <motion.div
          key={stage}
          initial={{ opacity: 0, x: stage === "verify" ? 15 : 0 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -15 }}
          transition={{ duration: 0.2, ease: [0.25, 1, 0.5, 1] }}
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {fieldSpecs.map((fieldSpec, index) => (
                <motion.div
                  key={fieldSpec.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.25,
                    delay: 0.1 + index * 0.05,
                    ease: [0.25, 1, 0.5, 1],
                  }}
                >
                  <FormField
                    control={form.control}
                    name={fieldSpec.key}
                    rules={{
                      required: fieldSpec.required,
                    }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingInput
                            label={fieldSpec.label}
                            autoCorrect="off"
                            autoCapitalize="none"
                            type={fieldSpec.type}
                            error={!!fieldState.error}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </motion.div>
              ))}
              <input type="submit" hidden />
            </form>
          </Form>
        </motion.div>
      </AnimatePresence>

      {/* Verification stage hint */}
      {stage === "verify" && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-sm text-muted-foreground mt-4 text-center"
        >
          Completa la verificación solicitada por tu banco.
        </motion.p>
      )}

      {/* Sandbox mode hint */}
      {appConfig?.isSandbox && stage === "auth" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-5 text-center"
        >
          <p className="text-xs text-muted-foreground">
            <FlaskConical className="inline h-3 w-3 mr-1 -translate-y-px" />
            Sandbox —{" "}
            <button
              type="button"
              onClick={() => {
                form.setValue("username", "username");
                form.setValue("password", "password");
              }}
              className="text-bank-primary hover:underline font-medium"
            >
              usar credenciales de prueba
            </button>
          </p>
        </motion.div>
      )}
    </ConnectStep>
  );
};

export default AuthStep;
